import React, { useEffect, useRef } from 'react';
// Tree-shakeable d3 named imports (~250KB saving over wildcard)
import { arc, pie, select, interpolate } from 'd3';
import { lightTheme as T } from '../../Constants/Colors';
import { fontFamily } from '../../Constants/Fonts';
const d3 = { arc, pie, select, interpolate };

// Compact risk-status donut. `data` is [{label, apples}] (High/Medium/Low);
// `colors` aligns 1:1 with data order. A faint full track ring sits behind the
// coloured segments so partial data still reads as a complete circle, and the
// centre shows the total count + a small caption. Segments sweep in on mount.
export default function StatusCard({ data = [], width = 96, colors, centerLabel = 'Total', showTotal = true, thickness = 10 }) {
    const svgRef = useRef(null);

    useEffect(() => {
        const size = width;
        const outer = size / 2 - 3;
        const inner = outer - thickness;
        const total = data.reduce((sum, d) => sum + Number(d.apples || 0), 0);

        const svg = d3.select(svgRef.current).attr('viewBox', `0 0 ${size} ${size}`);
        svg.selectAll('*').remove();

        const g = svg.append('g').attr('transform', `translate(${size / 2}, ${size / 2})`);

        // Faint full-circle track — always present for context.
        g.append('path')
            .attr('d', d3.arc().innerRadius(inner).outerRadius(outer).startAngle(0).endAngle(2 * Math.PI)())
            .attr('fill', T.border)
            .attr('opacity', 0.55);

        // Coloured segments on top (only when there's data), with a subtle
        // sweep-in animation from each slice's start angle.
        if (total > 0) {
            const arcGen = d3.arc().innerRadius(inner).outerRadius(outer).cornerRadius(4);
            const pieGen = d3.pie().sort(null).padAngle(0.06).value(d => Number(d.apples || 0));

            g.selectAll('path.seg')
                .data(pieGen(data))
                .join('path')
                .attr('class', 'seg')
                .attr('fill', (_, i) => (colors && colors[i]) || T.border)
                .transition()
                .duration(750)
                .attrTween('d', function (d) {
                    const i = d3.interpolate({ startAngle: d.startAngle, endAngle: d.startAngle }, d);
                    return (t) => arcGen(i(t));
                });
        }

        // Centre total + caption (optional — hidden for a hollow ring look)
        if (showTotal) {
            g.append('text')
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'central')
                .attr('y', centerLabel ? -5 : 0)
                .attr('font-family', fontFamily)
                .attr('font-size', Math.round(size * 0.2))
                .attr('font-weight', 500)
                .attr('fill', total === 0 ? T.textMuted : T.textPrimary)
                .text(total);

            if (centerLabel) {
                g.append('text')
                    .attr('text-anchor', 'middle')
                    .attr('dominant-baseline', 'central')
                    .attr('y', Math.round(size * 0.13))
                    .attr('font-family', fontFamily)
                    .attr('font-size', Math.max(8, Math.round(size * 0.1)))
                    .attr('font-weight', 600)
                    .attr('letter-spacing', '0.06em')
                    .attr('fill', T.textMuted)
                    .text(String(centerLabel).toUpperCase());
            }
        }
    }, [data, width, colors, centerLabel, showTotal, thickness]);

    return <svg ref={svgRef} width={width} height={width} />;
}
